import PropTypes from 'prop-types'
import {
  Box,
  Typography,
  TextField,
  Link,
  InputLabel,
  Alert,
} from '@mui/material'
import useTranslations from '../../../../i18n/useTranslations'
import useStyles from './styles'
import { ReactComponent as Weight } from '../../../../assets/icons/weight.svg'
import { ReactComponent as PackageClose } from '../../../../assets/icons/package-close.svg'
import { ReactComponent as Package } from '../../../../assets/icons/package.svg'
import Autocomplete from '../../../../ui/components/autocomplete'
import countryTelephoneData from 'country-telephone-data'
import { useDestinationCountries } from '../../../../services/destinationCountries'
import StyledPaper from '../../../../ui/components/StyledPaper'

const propTypes = {
  values: PropTypes.object.isRequired,
  errors: PropTypes.object.isRequired,
  handleChange: PropTypes.func.isRequired,
  handleBlur: PropTypes.func.isRequired,
}

const OriginPudo = ({ values, errors, handleChange, handleBlur }) => {
  const t = useTranslations()
  const classes = useStyles()
  const { data: countries } = useDestinationCountries()

  const countryPhones = countryTelephoneData.allCountries
    .sort((a, b) => {
      if (a.iso2 === 'es') return -1
      if (b.iso2 === 'es') return 1
      if (a.iso2 === 'pt') return -1
      if (b.iso2 === 'pt') return 1
      return a.name.localeCompare(b.name)
    })
    .map((country, index, arr) => {
      if (
        index === arr.length - 1 ||
        country.dialCode !== arr[index + 1].dialCode
      ) {
        let countryName = countries?.find(
          ({ code }) => code === country.iso2.toLocaleUpperCase(),
        )
        if (countryName)
          return {
            dialCode: `+${country.dialCode}`,
            countryCode: country.iso2,
            name: countryName ? countryName?.label : country?.name,
          }
        if (country.dialCode === '1')
          return {
            dialCode: `+${country.dialCode}`,
            countryCode: 'us',
            name: countryName
              ? countryName?.label
              : 'Estados Unidos de América',
          }
      }
      return null
    })
    .filter(country => country !== null)

  return (
    <Box className={classes.container}>
      <Typography variant="h3" className={classes.infoText2}>
        <strong>{t('shipping-send-form:origin-pudo:sender-info')}</strong>
      </Typography>
      <Box className={classes.form}>
        <Box className={classes.fieldsRow}>
          <TextField
            name="address.email"
            label={t('noun:email')}
            type="email"
            value={values.address.email}
            onChange={handleChange}
            onBlur={handleBlur}
            className={classes.field}
            required
            fullWidth
            margin="normal"
          />
          <Box className={classes.fieldMD}>
            <InputLabel required={true}>{t('noun:mobile-phone')}</InputLabel>
            <Box className={classes.fieldPrefixPhone}>
              {/* NOTE: select con prefijos de teléfono de ejemplo */}
              {/* TODO: cuando se aborde la tarea del prefijo, descomentar el Select. */}
              <Autocomplete
                noSearchIcon
                filterOptions={(options, { inputValue }) => {
                  if (!inputValue?.length) return options
                  return options.filter(option =>
                    `${option?.dialCode} | ${option?.name}`
                      .toLowerCase()
                      .includes(inputValue.toLowerCase()),
                  )
                }}
                options={countryPhones ?? []}
                value={values?.address?.phonePrefix ?? '+34'}
                isOptionEqualToValue={(option, value) =>
                  option?.dialCode === value
                }
                disableClearable
                name="address.phonePrefix"
                onChange={(_, phonePrefix) => {
                  if (phonePrefix === null) {
                    handleChange({
                      target: {
                        name: 'address.phonePrefix',
                        value: '+34',
                      },
                    })
                    return
                  }

                  handleChange({
                    target: {
                      name: 'address.phonePrefix',
                      value: phonePrefix?.dialCode,
                    },
                  })
                }}
                getOptionLabel={option => {
                  let findCountry = countryPhones.find(
                    ({ dialCode }) => dialCode === option,
                  )
                  if (option?.dialCode) return option?.dialCode
                  return `${findCountry?.dialCode}`
                }}
                renderOption={(props, option) => (
                  <Box
                    component="li"
                    {...props}
                    style={{ height: '100%', width: '100%', padding: 15 }}
                    key={`${option?.dialCode} | ${option?.name}`}
                  >
                    {`${option?.dialCode} | ${option?.name}`}
                  </Box>
                )}
                className={classes.fieldPrefixPhoneSelect}
                PaperComponent={props => <StyledPaper {...props} />}
                inputProps={{
                  placeholder: t('noun:phonePrefix'),
                }}
                onBlur={handleBlur}
              />
              <TextField
                name="address.phone"
                label={null}
                type="tel"
                className={classes.field2}
                error={errors?.address?.phone}
                required
                helperText={
                  errors?.address?.phone && errors?.address?.phone?.key
                    ? t(errors.address.phone.key)
                    : null
                }
                value={values?.address?.phone}
                onChange={handleChange}
                onBlur={handleBlur}
                // TODO: cuando se aborde la tarea del prefijo, utilizar la siguiente prop (className).
                // className={(classes.fieldMD, classes.fieldPrefixPhoneInput)}
                inputProps={{ readOnly: false, maxLength: 15 }}
              />
            </Box>
          </Box>
        </Box>
      </Box>
      <Typography variant="body1" className={classes.infoText}>
        {t('shipping-send-form:origin-pudo:email-instruction-1')}{' '}
        <strong>
          {t('shipping-send-form:origin-pudo:email-instruction-bold')}
        </strong>{' '}
        {t('shipping-send-form:origin-pudo:email-instruction-2')}
        <strong>
          {t('shipping-send-form:origin-pudo:email-instruction-bold-2')}
        </strong>{' '}
        {t('shipping-send-form:origin-pudo:email-instruction-3')}
      </Typography>
      <Typography variant="body1" className={classes.infoText}>
        {t('shipping-send-form:origin-pudo:info-1')}{' '}
        <Link
          href="https://www.cttexpress.com/localizador-de-puntos/"
          target="_blank"
          rel="noopener"
          className={classes.link}
        >
          {t('shipping-send-form:origin-pudo:available-points-link')}
        </Link>
      </Typography>
      <Alert severity="warning" sx={classes.alert}>
        <Typography variant="body2">
          {t('shipping-send-form:origin-pudo:alert')
            .split(':')
            .map((line, index, array) => (
              <span key={index}>
                {line}
                {index < array.length - 1 && ':'}
                {index < array.length - 1 && <br />}
              </span>
            ))}
        </Typography>
      </Alert>
    </Box>
  )
}

OriginPudo.propTypes = propTypes

export default OriginPudo
